// Mixpanel is intentionally loaded lazily to keep the initial UI bundle small.
// This avoids pulling `mixpanel-browser` (and its init work) into the first paint path.
const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN || '';

let mixpanelPromise = null;
let mixpanelInstance = null;
let mixpanelInitPromise = null;

const getMixpanel = async () => {
  if (!MIXPANEL_TOKEN) return null;
  if (mixpanelInstance) return mixpanelInstance;
  if (!mixpanelPromise) {
    mixpanelPromise = import('mixpanel-browser')
      .then((m) => m.default || m)
      .then((mp) => {
        mixpanelInstance = mp;
        return mp;
      });
  }
  return mixpanelPromise;
};

const ensureMixpanelInitialized = async () => {
  if (!MIXPANEL_TOKEN) return null;
  if (mixpanelInitPromise) return mixpanelInitPromise;
  mixpanelInitPromise = (async () => {
    const mp = await getMixpanel();
    if (!mp) return null;
    mp.init(MIXPANEL_TOKEN, {
      debug: process.env.NODE_ENV === 'development',
      track_pageview: false,
      persistence: 'localStorage',
      api_host: 'https://api.mixpanel.com',
      autocapture: true,
      record_sessions_percent: 100
    });
    return mp;
  })();
  return mixpanelInitPromise;
};

// Mixpanel Analytics Service
class MixpanelService {
  constructor() {
    this.isInitialized = false;
    this.userId = null;
  }

  // Initialize with user data
  init(userId, userProperties = {}) {
    this.userId = userId;
    this.isInitialized = true;
    
    // Load & initialize Mixpanel asynchronously, then identify.
    ensureMixpanelInitialized()
      .then((mp) => {
        if (!mp) return;
        mp.identify(userId);
        mp.people.set(userProperties);
        mp.track('User Logged In', {
          timestamp: new Date().toISOString(),
          user_id: userId,
          ...userProperties
        });
      })
      .catch((err) => {
        // Don't block UI on analytics failures.
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn('Mixpanel init failed:', err);
        }
      });
  }

  // Track events
  track(eventName, properties = {}) {
    if (!this.isInitialized) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn('Mixpanel not initialized. Call init() first.');
      }
      return;
    }

    const eventProperties = {
      timestamp: new Date().toISOString(),
      user_id: this.userId,
      ...properties
    };

    ensureMixpanelInitialized()
      .then((mp) => mp && mp.track(eventName, eventProperties))
      .catch(() => {
        // Swallow analytics errors; never break UI flows.
      });
  }

  // Track page views
  trackPageView(pageName, properties = {}) {
    this.track('Page View', {
      page_name: pageName,
      ...properties
    });
  }

  // Track user actions
  trackUserAction(action, properties = {}) {
    this.track(`User Action: ${action}`, properties);
  }

  // Track feature usage
  trackFeatureUsage(feature, properties = {}) {
    this.track(`Feature Used: ${feature}`, properties);
  }

  // Track errors
  trackError(error, context = {}) {
    this.track('Error Occurred', {
      error_message: error.message,
      error_stack: error.stack,
      ...context
    });
  }

  // Track GrabTime workflow events
  trackGrabTimeEvent(step, properties = {}) {
    this.track(`GrabTime: ${step}`, {
      workflow_step: step,
      ...properties
    });
  }

  // Track event creation
  trackEventCreation(eventData) {
    this.track('Event Created', {
      event_title: eventData.title,
      event_source: eventData.source || 'manual',
      has_location: !!eventData.location,
      has_participants: eventData.participant_ids?.length > 0,
      participant_count: eventData.participant_ids?.length || 0,
      is_recurring: !!eventData.recurring_pattern,
      category_id: eventData.category_id
    });
  }

  // Track friend actions
  trackFriendAction(action, properties = {}) {
    this.track(`Friend Action: ${action}`, properties);
  }

  // Track availability window actions
  trackAvailabilityAction(action, properties = {}) {
    this.track(`Availability Action: ${action}`, properties);
  }

  // Track restaurant finder usage
  trackRestaurantFinder(properties = {}) {
    this.track('Restaurant Finder Used', properties);
  }

  // Track onboarding progress
  trackOnboardingProgress(step, properties = {}) {
    this.track(`Onboarding: ${step}`, properties);
  }

  // Reset user session
  reset() {
    // Reset local state immediately; analytics reset can happen asynchronously.
    this.isInitialized = false;
    this.userId = null;

    ensureMixpanelInitialized()
      .then((mp) => mp && mp.reset())
      .catch(() => {
        // ignore
      });
  }

  // Get user properties
  getPeople() {
    // If Mixpanel isn't loaded yet, return null to avoid forcing a load.
    return mixpanelInstance ? mixpanelInstance.people : null;
  }
}

// Create singleton instance
const mixpanelService = new MixpanelService();

export default mixpanelService;
export { getMixpanel };
